/**
 * New marketing landing nav — design-system refresh.
 * Sticky frosted bar with logo, nav links, Sign in + Get started CTAs.
 * Pass `primaryNavItems` to replace marketing links with route-specific nav items.
 * Pass `organizationMenuItems`: one item renders as a direct link; multiple items render as a dropdown.
 */

import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { View, Text, Pressable, Image, Platform, useWindowDimensions, Animated } from 'react-native'
import { LinearGradient } from 'expo-linear-gradient'
import { Menu, X } from 'lucide-react-native'
import { usePathname, useRouter, type Href } from 'expo-router'
import { useSupabaseAuth } from '../../../src/shared/contexts/SupabaseAuthContext'
import { LanguageContext } from '../../../src/i18n'
import { useTheme } from '../../../src/theme/useTheme'
import { ASSET_PATHS, getCdnUrlExactKey } from '../../../constants/assets'
import {
  appendReturnUrlQuery,
  buildPublicSignInHref,
  siteMarketingReturnPathFromPathname,
} from '../../../src/signIn/helpers/postAuthRedirect.helper'
import { LANDING_PHONE_MAX_WIDTH } from '../../../src/landing/constants/landingMarketingBreakpoints.constants'
import { LANDING_SITE_NAV_ROUTES } from '../../../src/landing/constants/landingSiteNav.constants'
import { LandingSignedInControls } from './LandingSignedInControls'
import { LandingNavLinks } from './LandingNavLinks'
import { LandingNavMobileMenu } from './LandingNavMobileMenu'
import { getLandingNewNavStyles, landingNewNavWebBarStyle } from './LandingNewNav.styles'

interface LandingNewNavProps {
  onPressHome: () => void
  onPressFeatures?: () => void
  onPressHowItWorks?: () => void
  onPressPricing?: () => void
  onPressContact?: () => void
  /** Web org admin: hide header center links when the page uses the persistent sidebar (≥960px). */
  centerNavEmpty?: boolean
  primaryNavItems?: LandingNavItem[]
  organizationMenuItems?: OrganizationMenuItem[]
  headerRight?: ReactNode
}

export type LandingNavItem = {
  key: string
  label: string
  onPress: () => void
}

export type OrganizationMenuItem = {
  key: string
  label: string
  description: string
  onPress: () => void
}

const COMPACT_NAV_WIDTH = 1085
const PHONE_NAV_WIDTH = LANDING_PHONE_MAX_WIDTH

export function LandingNewNav({
  onPressHome,
  onPressFeatures,
  onPressHowItWorks,
  onPressPricing,
  onPressContact,
  centerNavEmpty,
  primaryNavItems,
  organizationMenuItems,
  headerRight,
}: LandingNewNavProps) {
  const router = useRouter()
  const theme = useTheme()
  const styles = useMemo(() => getLandingNewNavStyles(theme), [theme])
  const languageContext = useContext(LanguageContext)
  if (!languageContext) {
    throw new Error('LandingNewNav must be used within LanguageProvider')
  }
  const { t } = languageContext
  const pathname = usePathname()
  const { width } = useWindowDimensions()
  const { isLoaded, isSignedIn, user, signOut } = useSupabaseAuth()
  const [organizationsOpen, setOrganizationsOpen] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const closeOrganizationsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const shineAnim = useRef(new Animated.Value(-90)).current
  const isCompact = width > 0 && width < COMPACT_NAV_WIDTH
  const isPhone = width > 0 && width < PHONE_NAV_WIDTH
  const hasOrganizationMenu = !!organizationMenuItems?.length
  const organizationSingleLink =
    organizationMenuItems?.length === 1 ? organizationMenuItems[0] : null

  const marketingReturnPath = siteMarketingReturnPathFromPathname(pathname)
  const accountLabel = useMemo(() => {
    const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim()
    if (fullName) return fullName
    if (user?.username) return user.username
    return user?.primaryEmailAddress?.emailAddress ?? t.siteNav.signedInNameFallback
  }, [
    t.siteNav.signedInNameFallback,
    user?.firstName,
    user?.lastName,
    user?.primaryEmailAddress?.emailAddress,
    user?.username,
  ])
  const accountInitial = accountLabel.trim().charAt(0).toUpperCase()

  useEffect(() => {
    const animation = Animated.loop(
      Animated.sequence([
        Animated.delay(10000),
        Animated.timing(shineAnim, { toValue: 180, duration: 950, useNativeDriver: true }),
        Animated.timing(shineAnim, { toValue: -90, duration: 0, useNativeDriver: true }),
      ]),
    )
    animation.start()
    return () => animation.stop()
  }, [shineAnim])

  useEffect(
    () => () => {
      if (closeOrganizationsTimerRef.current) {
        clearTimeout(closeOrganizationsTimerRef.current)
      }
    },
    [],
  )

  const goSignIn = useCallback(() => {
    router.push(appendReturnUrlQuery(LANDING_SITE_NAV_ROUTES.SIGN_IN, marketingReturnPath) as Href)
  }, [router, marketingReturnPath])

  const goGetStarted = useCallback(() => {
    router.push(buildPublicSignInHref({ signup: true, returnUrl: marketingReturnPath }) as Href)
  }, [router, marketingReturnPath])

  const goApp = useCallback(() => {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.open(LANDING_SITE_NAV_ROUTES.APP, '_blank', 'noopener,noreferrer')
      return
    }
    router.push(LANDING_SITE_NAV_ROUTES.APP as Href)
  }, [router])

  const openOrganizations = useCallback(() => {
    if (!hasOrganizationMenu) return
    if (closeOrganizationsTimerRef.current) {
      clearTimeout(closeOrganizationsTimerRef.current)
      closeOrganizationsTimerRef.current = null
    }
    setOrganizationsOpen(true)
  }, [hasOrganizationMenu])

  const scheduleCloseOrganizations = useCallback(() => {
    if (isCompact) return
    if (closeOrganizationsTimerRef.current) {
      clearTimeout(closeOrganizationsTimerRef.current)
    }
    closeOrganizationsTimerRef.current = setTimeout(() => {
      setOrganizationsOpen(false)
      closeOrganizationsTimerRef.current = null
    }, 260)
  }, [isCompact])

  const toggleOrganizations = useCallback(() => {
    if (!hasOrganizationMenu) return
    setOrganizationsOpen((open) => !open)
  }, [hasOrganizationMenu])

  const handleOrganizationItemPress = useCallback((onPress: () => void) => {
    setOrganizationsOpen(false)
    onPress()
  }, [])

  const handleSignOut = useCallback(() => { void signOut() }, [signOut])
  const closeMobileMenu = useCallback(() => setMobileMenuOpen(false), [])

  return (
    <View style={[styles.bar, landingNewNavWebBarStyle]}>
      <View style={[styles.inner, isCompact && styles.innerCompact, isPhone && styles.innerPhone]}>
        <Pressable
          accessibilityRole="link"
          accessibilityLabel={t.siteNav.home}
          onPress={onPressHome}
          style={({ hovered, pressed }) => [
            styles.logoRow,
            isPhone && styles.logoRowPhone,
            (hovered || pressed) && styles.logoRowPressed,
          ]}
        >
          <View style={styles.logoMark}>
            <Image
              source={{ uri: getCdnUrlExactKey(ASSET_PATHS.LANDING_LOGO) }}
              style={styles.logoMarkImage}
              resizeMode="cover"
              accessibilityIgnoresInvertColors
            />
          </View>
          <Text style={styles.logoText}>zporti</Text>
        </Pressable>

        {!isPhone && !isCompact && (
          <LandingNavLinks
            isCompact={isCompact}
            suppressCenterNav={centerNavEmpty}
            onPressFeatures={onPressFeatures}
            onPressHowItWorks={onPressHowItWorks}
            onPressPricing={onPressPricing}
            onPressContact={onPressContact}
            primaryNavItems={primaryNavItems}
            organizationSingleLink={organizationSingleLink}
            hasOrganizationMenu={hasOrganizationMenu}
            organizationMenuItems={organizationMenuItems}
            organizationsOpen={organizationsOpen}
            openOrganizations={openOrganizations}
            scheduleCloseOrganizations={scheduleCloseOrganizations}
            toggleOrganizations={toggleOrganizations}
            onOrganizationItemPress={handleOrganizationItemPress}
            t={t}
          />
        )}

        <View style={[styles.ctaRow, isCompact && styles.ctaRowCompact, isPhone && styles.ctaRowPhone]}>
          {isLoaded && isSignedIn ? (
            <>
              {!isPhone && (
                <LandingSignedInControls
                  accountInitial={accountInitial}
                  accountLabel={accountLabel}
                  signedInAsLabel={t.siteNav.signedInAs}
                  signOutLabel={t.siteNav.signOut}
                  onPressSignOut={handleSignOut}
                />
              )}
              <Pressable accessibilityRole="button" onPress={goApp} style={styles.openAppPressable}>
                <LinearGradient
                  colors={['#9F7AEA', '#6B46C1']}
                  start={{ x: 0.5, y: 0 }}
                  end={{ x: 0.5, y: 1 }}
                  style={[
                    styles.openAppGradient,
                    isCompact && styles.openAppGradientCompact,
                    isPhone && styles.openAppGradientPhone,
                  ]}
                >
                  <Animated.View
                    pointerEvents="none"
                    style={[styles.openAppShine, { transform: [{ translateX: shineAnim }, { rotate: '18deg' }] }]}
                  />
                  <Text style={[
                    styles.primaryBtnText,
                    isCompact && styles.primaryBtnTextCompact,
                    isPhone && styles.primaryBtnTextPhone,
                  ]}>
                    {t.siteNav.openApp}
                  </Text>
                </LinearGradient>
              </Pressable>
            </>
          ) : (
            <>
              {!isPhone ? (
                <Pressable
                  accessibilityRole="link"
                  onPress={goSignIn}
                  style={[styles.ghostBtn, isPhone && styles.ghostBtnPhone]}
                >
                  {({ hovered }) => (
                    <Text style={[styles.ghostBtnText, isPhone && styles.ghostBtnTextPhone, hovered && styles.ghostBtnHovered]}>
                      {t.siteNav.signIn}
                    </Text>
                  )}
                </Pressable>
              ) : null}
              <Pressable accessibilityRole="button" onPress={goGetStarted} style={styles.openAppPressable}>
                <LinearGradient
                  colors={['#9F7AEA', '#6B46C1']}
                  start={{ x: 0.5, y: 0 }}
                  end={{ x: 0.5, y: 1 }}
                  style={[
                    styles.openAppGradient,
                    isCompact && styles.openAppGradientCompact,
                    isPhone && styles.openAppGradientPhone,
                  ]}
                >
                  <Animated.View
                    pointerEvents="none"
                    style={[styles.openAppShine, { transform: [{ translateX: shineAnim }, { rotate: '18deg' }] }]}
                  />
                  <Text style={[
                    styles.primaryBtnText,
                    isCompact && styles.primaryBtnTextCompact,
                    isPhone && styles.primaryBtnTextPhone,
                  ]}>
                    {t.siteNav.createAccount}
                  </Text>
                </LinearGradient>
              </Pressable>
            </>
          )}
          {headerRight && !isPhone ? (
            <View style={[styles.headerRightSlot, isCompact && styles.headerRightSlotCompact]}>
              {headerRight}
            </View>
          ) : null}
        </View>

        {isPhone && headerRight ? (
          <View style={styles.headerRightSlotPhone}>{headerRight}</View>
        ) : null}

        {!isPhone && isCompact && (
          <View style={styles.navLinksCompactSlot}>
            <LandingNavLinks
              isCompact={isCompact}
              suppressCenterNav={centerNavEmpty}
              onPressFeatures={onPressFeatures}
              onPressHowItWorks={onPressHowItWorks}
              onPressPricing={onPressPricing}
              onPressContact={onPressContact}
              primaryNavItems={primaryNavItems}
              organizationSingleLink={organizationSingleLink}
              hasOrganizationMenu={hasOrganizationMenu}
              organizationMenuItems={organizationMenuItems}
              organizationsOpen={organizationsOpen}
              openOrganizations={openOrganizations}
              scheduleCloseOrganizations={scheduleCloseOrganizations}
              toggleOrganizations={toggleOrganizations}
              onOrganizationItemPress={handleOrganizationItemPress}
              t={t}
            />
          </View>
        )}

        {isPhone && (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={mobileMenuOpen ? t.a11y.closeMenu : t.a11y.openMenu}
            onPress={() => setMobileMenuOpen((open) => !open)}
            style={[styles.hamburgerBtn, mobileMenuOpen && styles.hamburgerBtnActive]}
          >
            {mobileMenuOpen ? <X size={20} color={theme.primaryDark} /> : <Menu size={20} color={theme.primaryDark} />}
          </Pressable>
        )}
      </View>

      {isPhone && mobileMenuOpen && (
        <LandingNavMobileMenu
          onPressFeatures={onPressFeatures}
          onPressHowItWorks={onPressHowItWorks}
          onPressPricing={onPressPricing}
          onPressContact={onPressContact}
          primaryNavItems={primaryNavItems}
          organizationMenuItems={organizationMenuItems}
          hasOrganizationMenu={hasOrganizationMenu}
          closeMobileMenu={closeMobileMenu}
          goSignIn={goSignIn}
          handleSignOut={handleSignOut}
          isLoaded={isLoaded}
          isSignedIn={isSignedIn}
          t={t}
        />
      )}
    </View>
  )
}
